// Warning banner for a run whose input data ended well before the run itself
// executed — a stale upstream feed, not a broken run.
//
// Three environment-difference questions in one session read as data bugs
// before this existed. Grading lives in staleness.ts; this file is only the
// two visual registers.
//
// Brooklinen is the case that motivated it — its sales feed froze at
// 2026-06-21 and did not unfreeze until 2026-08-11, seven weeks in which
// every run executed correctly against data that had stopped moving.
import { SnapshotTokens, useSnapshotTokens } from "./primitives";
import { Staleness, weeksPhrase } from "./staleness";

/** Muted single line — the same register as the other soft notes on the page. */
const SoftNote = ({ days, t }: { days: number; t: SnapshotTokens }) => (
  <div style={{ fontSize: 12, color: t.ink3 }}>
    Input data ends <b style={{ fontWeight: 600 }}>{days} days</b> before this
    run executed{weeksPhrase(days)}. The run is fine — its upstream feed was
    behind.
  </div>
);

export const StalenessBanner = ({ staleness }: { staleness: Staleness }) => {
  const t = useSnapshotTokens();
  if (staleness.level === "silent") {
    return null;
  }
  if (staleness.level === "note") {
    return <SoftNote days={staleness.days} t={t} />;
  }
  return (
    <div
      style={{
        border: `1px solid ${t.orangeInk}55`,
        background: t.orange1,
        borderRadius: 8,
        padding: "10px 14px",
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: 12.5, fontWeight: 700, color: t.orangeInk }}>
        Input data ends {staleness.days} days before this run
        {weeksPhrase(staleness.days)}
      </span>
      <span style={{ fontSize: 12, color: t.ink3 }}>
        The run itself executed correctly — every figure below is a faithful
        read of the data it was given. The upstream feed had stopped updating.
      </span>
    </div>
  );
};
